import React, { useState, useEffect } from 'react';
import { getMenuItems, saveMenuItems, DEFAULT_MENU_ITEMS } from '../services/menuService';

const NavigationMenuAdmin = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');


  // New item form
  const [newItemLabel, setNewItemLabel] = useState('');
  const [newItemPath, setNewItemPath] = useState('');

  // New child form
  const [activeParentForChild, setActiveParentForChild] = useState(null);
  const [newChildLabel, setNewChildLabel] = useState('');
  const [newChildPath, setNewChildPath] = useState('');

  // Editing state for top-level item
  const [editingParentIndex, setEditingParentIndex] = useState(null);
  const [editingParentLabel, setEditingParentLabel] = useState('');
  const [editingParentPath, setEditingParentPath] = useState('');

  // Editing state for submenu child
  const [editingChild, setEditingChild] = useState(null); // { parentIdx, childIdx, label, path }

  useEffect(() => {
    fetchMenu();
  }, []);

  const fetchMenu = async () => {
    setLoading(true);
    try {
      const items = await getMenuItems();
      setMenuItems(items);
      setError('');
    } catch (err) {
      setError('Failed to fetch navigation menu items');
    }
    setLoading(false);
  };

  // Helper to remove any numbers (e.g., "2.1 ", "1. ")
  const cleanLabel = (text) => {
    if (!text) return '';
    return text.replace(/^\d+(\.\d+)*\.?\s*/, '').trim();
  };

  const handleSaveAll = async () => {
    setSaving(true);
    setError('');
    try {
      const sanitized = menuItems.map((item) => ({
        ...item,
        label: cleanLabel(item.label),
        children: Array.isArray(item.children)
          ? item.children.map((child) => ({
              ...child,
              label: cleanLabel(child.label)
            }))
          : []
      }));
      await saveMenuItems(sanitized);
      setMenuItems(sanitized);
      setSuccess('Navigation menu successfully saved! Frontend navbar will now update.');
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.message || 'Failed to save menu changes.');
    }
    setSaving(false);
  };

  const handleResetToDefault = async () => {
    if (window.confirm('Reset navigation menu to the official 9-section academic structure? Any custom links will be replaced.')) {
      setSaving(true);
      try {
        await saveMenuItems(DEFAULT_MENU_ITEMS);
        setMenuItems(DEFAULT_MENU_ITEMS);
        setSuccess('Navigation menu reset to default 9-section hierarchy!');
        setTimeout(() => setSuccess(''), 4000);
      } catch (err) {
        setError(err.message || 'Failed to reset menu.');
      }
      setSaving(false);
    }
  };

  // Move top level item
  const moveItem = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= menuItems.length) return;
    const updated = [...menuItems];
    const temp = updated[index];
    updated[index] = updated[targetIndex];
    updated[targetIndex] = temp;
    setMenuItems(updated);
  };

  // Edit top level item
  const startEditParent = (idx) => {
    setEditingParentIndex(idx);
    setEditingParentLabel(menuItems[idx].label);
    setEditingParentPath(menuItems[idx].path);
    setEditingChild(null);
  };

  const saveEditParent = () => {
    if (!editingParentLabel.trim() || !editingParentPath.trim()) return;
    const updated = [...menuItems];
    updated[editingParentIndex] = {
      ...updated[editingParentIndex],
      label: cleanLabel(editingParentLabel),
      path: editingParentPath.trim()
    };
    setMenuItems(updated);
    setEditingParentIndex(null);
  };

  // Toggle top level item active / non-active with immediate persistence
  const toggleParentActive = async (parentIdx) => {
    const updated = [...menuItems];
    const isCurrentlyActive = updated[parentIdx].isActive !== false;
    const newStatus = !isCurrentlyActive;
    updated[parentIdx] = {
      ...updated[parentIdx],
      isActive: newStatus
    };
    setMenuItems(updated);
    
    // Auto-save toggle immediately so user never wonders if it took effect
    try {
      await saveMenuItems(updated);
      setSuccess(`"${cleanLabel(updated[parentIdx].label)}" is now ${newStatus ? 'Active (Visible on Header)' : 'Inactive (Hidden from Header)'}!`);
      setTimeout(() => setSuccess(''), 3500);
    } catch (e) {
      setError(e.message || 'Could not auto-save. Please click "Save Menu".');
    }
  };

  // Toggle child item active / non-active with immediate persistence
  const toggleChildActive = async (parentIdx, childIdx) => {
    const updated = [...menuItems];
    const children = [...updated[parentIdx].children];
    const isCurrentlyActive = children[childIdx].isActive !== false;
    const newStatus = !isCurrentlyActive;
    children[childIdx] = {
      ...children[childIdx],
      isActive: newStatus
    };
    updated[parentIdx] = {
      ...updated[parentIdx],
      children
    };
    setMenuItems(updated);
    
    // Auto-save toggle immediately so user never wonders if it took effect
    try {
      await saveMenuItems(updated);
      setSuccess(`Submenu "${cleanLabel(children[childIdx].label)}" is now ${newStatus ? 'Active (Visible)' : 'Inactive (Hidden)'}!`);
      setTimeout(() => setSuccess(''), 3500);
    } catch (e) {
      setError(e.message || 'Could not auto-save. Please click "Save Menu".');
    }
  };

  // Delete top level item
  const deleteItem = (index) => {
    if (window.confirm(`Delete menu item "${menuItems[index].label}"?`)) {
      const updated = menuItems.filter((_, idx) => idx !== index);
      setMenuItems(updated);
    }
  };

  // Add new top level item
  const handleAddTopItem = (e) => {
    e.preventDefault();
    if (!newItemLabel.trim() || !newItemPath.trim()) return;
    const newItem = {
      id: Date.now().toString(),
      label: cleanLabel(newItemLabel),
      path: newItemPath.trim(),
      isActive: true,
      children: []
    };
    setMenuItems([...menuItems, newItem]);
    setNewItemLabel('');
    setNewItemPath('');
  };

  // Add new child item
  const handleAddChild = (parentIndex) => {
    if (!newChildLabel.trim() || !newChildPath.trim()) return;
    const updated = [...menuItems];
    const parent = updated[parentIndex];
    const children = parent.children || [];
    parent.children = [
      ...children,
      {
        id: `${parent.id || parentIndex}.${Date.now()}`,
        label: cleanLabel(newChildLabel),
        path: newChildPath.trim(),
        isActive: true
      }
    ];
    setMenuItems(updated);
    setNewChildLabel('');
    setNewChildPath('');
    setActiveParentForChild(null);
  };

  // Edit child item
  const startEditChild = (parentIdx, childIdx) => {
    const child = menuItems[parentIdx].children[childIdx];
    setEditingChild({
      parentIdx,
      childIdx,
      label: child.label,
      path: child.path
    });
    setEditingParentIndex(null);
  };

  const saveEditChild = () => {
    if (!editingChild || !editingChild.label.trim() || !editingChild.path.trim()) return;
    const { parentIdx, childIdx, label, path } = editingChild;
    const updated = [...menuItems];
    const children = [...updated[parentIdx].children];
    children[childIdx] = {
      ...children[childIdx],
      label: cleanLabel(label),
      path: path.trim()
    };
    updated[parentIdx] = {
      ...updated[parentIdx],
      children
    };
    setMenuItems(updated);
    setEditingChild(null);
  };

  // Delete child item
  const deleteChild = (parentIndex, childIndex) => {
    const updated = [...menuItems];
    updated[parentIndex].children = updated[parentIndex].children.filter((_, idx) => idx !== childIndex);
    setMenuItems(updated);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b pb-4 mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Navigation Menu Items</h2>
            <p className="text-sm text-gray-500 mt-1">
              Manage the navigation menu items and dropdown submenus. Use the <strong className="text-gray-700">Active / Inactive</strong> toggle on any menu or submenu to show or hide it from the website header without deleting it. Click <strong className="text-indigo-600">Save Menu</strong> to publish changes.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleResetToDefault}
              className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-md text-xs font-semibold"
            >
              Reset to Official 9 Sections
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={handleSaveAll}
              className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-xs font-bold shadow-xs disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Menu'}
            </button>
          </div>
        </div>

        {error && <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-sm">{error}</div>}
        {success && <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded text-sm">{success}</div>}

        {loading ? (
          <div className="text-center py-10 text-gray-500">Loading menu configuration...</div>
        ) : (
          <div className="space-y-4">
            {menuItems.map((item, parentIdx) => (
              <div
                key={item.id || parentIdx}
                className={`border rounded-xl p-4 transition-all space-y-3 ${
                  item.isActive !== false ? 'border-gray-200 bg-gray-50/50' : 'border-gray-300 bg-gray-100/70 opacity-80'
                }`}
              >
                {/* Top Level Item Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-3 rounded-lg border border-gray-100 shadow-2xs">
                  <div className="flex items-center gap-3 flex-1">
                    <span className="w-6 h-6 rounded-full bg-blue-100 text-[#03478E] font-bold text-xs flex items-center justify-center flex-shrink-0">
                      {parentIdx + 1}
                    </span>
                    <div className="flex-1">
                      {editingParentIndex === parentIdx ? (
                        <div className="flex flex-col sm:flex-row items-center gap-2">
                          <input
                            type="text"
                            value={editingParentLabel}
                            onChange={(e) => setEditingParentLabel(e.target.value)}
                            className="text-xs px-2.5 py-1.5 border border-blue-400 rounded-md font-bold w-full sm:w-1/2 focus:ring-1 focus:ring-blue-500"
                            placeholder="Menu Label"
                          />
                          <input
                            type="text"
                            value={editingParentPath}
                            onChange={(e) => setEditingParentPath(e.target.value)}
                            className="text-xs px-2.5 py-1.5 border border-blue-400 rounded-md font-mono text-gray-600 w-full sm:w-1/2 focus:ring-1 focus:ring-blue-500"
                            placeholder="URL Path"
                          />
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={saveEditParent}
                              className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-bold"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingParentIndex(null)}
                              className="px-2 py-1.5 text-gray-500 hover:text-gray-700 text-xs"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className={`font-bold text-sm ${
                              item.isActive !== false ? 'text-gray-900' : 'text-gray-500 line-through'
                            }`}
                          >
                            {item.label}
                          </span>
                          <span className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-0.5 rounded">
                            {item.path}
                          </span>
                          {item.children && item.children.length > 0 && (
                            <span className="text-[11px] font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                              {item.children.length} submenu {item.children.length === 1 ? 'item' : 'items'}
                            </span>
                          )}
                          {item.isActive === false && (
                            <span className="text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                              Inactive (Hidden)
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions for Top Level */}
                  <div className="flex items-center gap-2 self-end sm:self-center flex-wrap">
                    {/* Active / Inactive Toggle Button */}
                    <button
                      type="button"
                      onClick={() => toggleParentActive(parentIdx)}
                      className={`px-2.5 py-1 text-xs font-bold rounded-full border transition-all flex items-center gap-1.5 shadow-2xs ${
                        item.isActive !== false
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100'
                          : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
                      }`}
                      title={
                        item.isActive !== false
                          ? 'Active: Shown on website header. Click to make Inactive.'
                          : 'Inactive: Hidden from website header. Click to make Active.'
                      }
                    >
                      <span
                        className={`w-2 h-2 rounded-full ${item.isActive !== false ? 'bg-emerald-500' : 'bg-gray-400'}`}
                      ></span>
                      {item.isActive !== false ? 'Active' : 'Inactive'}
                    </button>

                    <button
                      type="button"
                      disabled={parentIdx === 0}
                      onClick={() => moveItem(parentIdx, -1)}
                      className="p-1.5 text-gray-500 hover:text-gray-900 disabled:opacity-30"
                      title="Move Up"
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      disabled={parentIdx === menuItems.length - 1}
                      onClick={() => moveItem(parentIdx, 1)}
                      className="p-1.5 text-gray-500 hover:text-gray-900 disabled:opacity-30"
                      title="Move Down"
                    >
                      ▼
                    </button>
                    <button
                      type="button"
                      onClick={() => (editingParentIndex === parentIdx ? setEditingParentIndex(null) : startEditParent(parentIdx))}
                      className="px-2.5 py-1 text-xs font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded"
                    >
                      {editingParentIndex === parentIdx ? 'Cancel' : 'Edit'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveParentForChild(activeParentForChild === parentIdx ? null : parentIdx)}
                      className="px-2.5 py-1 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded"
                    >
                      + Submenu
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteItem(parentIdx)}
                      className="px-2.5 py-1 text-xs font-semibold text-red-600 hover:bg-red-50 rounded"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {/* Submenu Children List */}
                {item.children && item.children.length > 0 && (
                  <div className="pl-6 space-y-2">
                    {item.children.map((child, childIdx) => {
                      const isEditingThisChild =
                        editingChild?.parentIdx === parentIdx && editingChild?.childIdx === childIdx;

                      return (
                        <div
                          key={child.id || childIdx}
                          className={`flex items-center justify-between p-2.5 rounded-lg border text-xs transition-all ${
                            child.isActive !== false
                              ? 'bg-white border-gray-100'
                              : 'bg-gray-100/80 border-gray-200 opacity-75'
                          }`}
                        >
                          {isEditingThisChild ? (
                            <div className="flex flex-col sm:flex-row items-center gap-2 flex-1 mr-2">
                              <span className="text-gray-400 font-mono text-[10px]">
                                {parentIdx + 1}.{childIdx + 1}
                              </span>
                              <input
                                type="text"
                                value={editingChild.label}
                                onChange={(e) => setEditingChild({ ...editingChild, label: e.target.value })}
                                className="text-xs px-2 py-1 border border-blue-400 rounded-md font-semibold w-full sm:w-1/2 focus:ring-1 focus:ring-blue-500"
                                placeholder="Submenu Label"
                              />
                              <input
                                type="text"
                                value={editingChild.path}
                                onChange={(e) => setEditingChild({ ...editingChild, path: e.target.value })}
                                className="text-xs px-2 py-1 border border-blue-400 rounded-md font-mono text-gray-600 w-full sm:w-1/2 focus:ring-1 focus:ring-blue-500"
                                placeholder="Path / Anchor"
                              />
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={saveEditChild}
                                  className="px-2.5 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-[11px] font-bold"
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEditingChild(null)}
                                  className="px-2 py-1 text-gray-500 text-[11px]"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 flex-wrap flex-1">
                              <span className="text-gray-400 font-mono text-[10px]">
                                {parentIdx + 1}.{childIdx + 1}
                              </span>
                              <span
                                className={`font-semibold ${
                                  child.isActive !== false ? 'text-gray-800' : 'text-gray-500 line-through'
                                }`}
                              >
                                {child.label}
                              </span>
                              <span className="text-gray-400 font-mono text-[11px]">{child.path}</span>
                              {child.isActive === false && (
                                <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                                  Inactive
                                </span>
                              )}
                            </div>
                          )}

                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            {/* Active / Inactive Toggle for Child */}
                            <button
                              type="button"
                              onClick={() => toggleChildActive(parentIdx, childIdx)}
                              className={`px-2 py-0.5 text-[11px] font-semibold rounded-full border transition-all flex items-center gap-1 ${
                                child.isActive !== false
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100'
                                  : 'bg-gray-100 text-gray-500 border-gray-300 hover:bg-gray-200'
                              }`}
                              title={
                                child.isActive !== false
                                  ? 'Active: Shown in dropdown. Click to hide.'
                                  : 'Inactive: Hidden from dropdown. Click to show.'
                              }
                            >
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${
                                  child.isActive !== false ? 'bg-emerald-500' : 'bg-gray-400'
                                }`}
                              ></span>
                              {child.isActive !== false ? 'Active' : 'Hidden'}
                            </button>

                            <button
                              type="button"
                              onClick={() => (isEditingThisChild ? setEditingChild(null) : startEditChild(parentIdx, childIdx))}
                              className="text-indigo-600 hover:text-indigo-800 px-2 py-0.5 rounded text-[11px] font-medium"
                            >
                              {isEditingThisChild ? 'Close' : 'Edit'}
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteChild(parentIdx, childIdx)}
                              className="text-red-500 hover:text-red-700 px-2 py-0.5 rounded text-[11px]"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Add Submenu Form */}
                {activeParentForChild === parentIdx && (
                  <div className="pl-6 pt-2">
                    <div className="p-3 bg-blue-50/60 rounded-xl border border-blue-100 flex flex-col sm:flex-row items-center gap-2">
                      <input
                        type="text"
                        placeholder="Submenu Label (e.g. Mission, Vision & Scope)"
                        value={newChildLabel}
                        onChange={(e) => setNewChildLabel(e.target.value)}
                        className="text-xs px-3 py-1.5 border border-gray-300 rounded-md w-full sm:w-1/2"
                      />
                      <input
                        type="text"
                        placeholder="Path / Anchor (e.g. /about-us#mission-vision-scope)"
                        value={newChildPath}
                        onChange={(e) => setNewChildPath(e.target.value)}
                        className="text-xs px-3 py-1.5 border border-gray-300 rounded-md w-full sm:w-1/3"
                      />
                      <div className="flex items-center gap-2 w-full sm:w-auto">
                        <button
                          type="button"
                          onClick={() => handleAddChild(parentIdx)}
                          className="px-3 py-1.5 bg-[#03478E] text-white rounded text-xs font-bold"
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          onClick={() => setActiveParentForChild(null)}
                          className="px-2 py-1.5 text-gray-500 text-xs"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Add New Top Level Menu Item Form */}
            <form onSubmit={handleAddTopItem} className="mt-6 p-4 border border-dashed border-gray-300 rounded-xl bg-gray-50 space-y-3">
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-700">Add New Top-Level Menu Tab</h3>
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
                <input
                  type="text"
                  placeholder="Menu Label (e.g. Special Announcements)"
                  value={newItemLabel}
                  onChange={(e) => setNewItemLabel(e.target.value)}
                  className="sm:col-span-5 text-xs px-3 py-2 border border-gray-300 rounded-md"
                />
                <input
                  type="text"
                  placeholder="URL Path (e.g. /announcements)"
                  value={newItemPath}
                  onChange={(e) => setNewItemPath(e.target.value)}
                  className="sm:col-span-5 text-xs px-3 py-2 border border-gray-300 rounded-md"
                />
                <button
                  type="submit"
                  className="sm:col-span-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-xs font-bold"
                >
                  + Add Item
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default NavigationMenuAdmin;
